// FILE: apps/admin/src/App.tsx
// VERSION: 2.3.0
// START_MODULE_CONTRACT
//   PURPOSE: Main Admin Panel application shell in MarketPlace dark-obsidian style with responsive off-canvas mobile sidebar, SVG sidebar navigation, dedicated /pricing route, expandable groups, 21 operator routes, sticky topbar, magic-link auth, and stealth 404 camouflage with 3-tap touch / double-click / Ctrl+Shift+L Telegram DM login.
//   SCOPE: App root component, StealthNotFound login screen, isMobileShellViewport, and advanceSecretTapCount.
//   DEPENDS: ./api.js, ./display-currency.js, ./toast.js, ./components/*, ./pages/*
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   App - Admin SPA root shell with responsive off-canvas mobile drawer, live operator topbar, and 21 routes
//   advanceSecretTapCount - Pure multi-tap counter unlocking hidden DM login on mobile touch screens within 900ms window
//   isMobileShellViewport - Breakpoint predicate (<= 1024px) switching sidebar from sticky column to off-canvas drawer
// END_MODULE_MAP

import { Component, type ErrorInfo, type ReactNode, useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { api, exchangeLogin, startDmLogin, token, verifyDmLogin } from './api.js';
import { GlobalSearch } from './components/GlobalSearch.js';
import { AdminIcon } from './components/Icons.js';
import { OperatorAlertsMenu } from './components/OperatorAlertsMenu.js';
import {
  DisplayCurrencyProvider,
  DisplayCurrencyToggle,
  GramUsdRateBadge,
} from './display-currency.js';
import { Alerts } from './pages/Alerts.js';
import { AuditLogs } from './pages/AuditLogs.js';
import { AuthSessions } from './pages/AuthSessions.js';
import { BalanceAdjust } from './pages/BalanceAdjust.js';
import { BanAppeals } from './pages/BanAppeals.js';
import { Banners } from './pages/Banners.js';
import { Broadcasts } from './pages/Broadcasts.js';
import { Catalog } from './pages/Catalog.js';
import { Dashboard } from './pages/Dashboard.js';
import { FragmentSession } from './pages/FragmentSession.js';
import { Games } from './pages/Games.js';
import { Orders } from './pages/Orders.js';
import { Payments } from './pages/Payments.js';
import { Pricing } from './pages/Pricing.js';
import { Quests } from './pages/Quests.js';
import { Referrals } from './pages/Referrals.js';
import { Sessions } from './pages/Sessions.js';
import { Settings } from './pages/Settings.js';
import { SupportTickets } from './pages/SupportTickets.js';
import { Transactions } from './pages/Transactions.js';
import { Treasury } from './pages/Treasury.js';
import { Users } from './pages/Users.js';
import { ToastProvider } from './toast.js';

interface NavEntry {
  id: string;
  label: string;
  to?: string;
  end?: boolean;
  icon: ReactNode;
  children?: Array<{ to: string; label: string }>;
}

const NAV_ENTRIES: NavEntry[] = [
  {
    id: 'dashboard',
    label: 'Дашборд',
    to: '/',
    end: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1V9.5z" />
      </svg>
    ),
  },
  {
    id: 'users-group',
    label: 'Пользователи',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    children: [
      { to: '/users', label: 'Все пользователи' },
      { to: '/appeals', label: 'Апелляции банов' },
      { to: '/referrals', label: 'Реферальная сеть' },
    ],
  },
  {
    id: 'orders-group',
    label: 'Заказы',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    ),
    children: [
      { to: '/orders', label: 'Список заказов' },
      { to: '/payments', label: 'Платежи СБП Т-Банк' },
    ],
  },
  {
    id: 'catalog-api',
    label: 'Каталог и API',
    to: '/catalog',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
      </svg>
    ),
  },
  {
    id: 'catalog-games',
    label: 'Кейсы и мини-игры',
    to: '/games',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="15" x2="15" y2="15" />
      </svg>
    ),
  },
  {
    id: 'quests',
    label: 'Задания и квесты',
    to: '/quests',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
  },
  {
    id: 'finance-group',
    label: 'Баланс и финансы',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
      </svg>
    ),
    children: [
      { to: '/treasury', label: 'Казначейство & GRAM' },
      { to: '/transactions', label: 'Леджер (RUB / GRAM)' },
      { to: '/balance-adjust', label: 'Коррекция баланса' },
    ],
  },
  {
    id: 'pricing',
    label: 'Цены и тарифы',
    to: '/pricing',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="5" x2="5" y2="19" />
        <circle cx="6.5" cy="6.5" r="2.5" />
        <circle cx="17.5" cy="17.5" r="2.5" />
      </svg>
    ),
  },
  {
    id: 'promo-banners',
    label: 'Баннеры и промо',
    to: '/banners',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
  },
  {
    id: 'disputes-support',
    label: 'Жалобы и споры',
    to: '/support',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  },
  {
    id: 'notifications-group',
    label: 'Уведомления',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
    children: [
      { to: '/alerts', label: 'Алерты и Runbook' },
      { to: '/broadcasts', label: 'Рассылки в боте' },
    ],
  },
  {
    id: 'integrations-group',
    label: 'API и интеграции',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    children: [
      { to: '/sessions', label: 'Сессия Т-Банка' },
      { to: '/fragment', label: 'Сессия Fragment' },
      { to: '/auth-sessions', label: 'Доступы и роли' },
    ],
  },
  {
    id: 'settings',
    label: 'Настройки',
    to: '/settings',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
  },
  {
    id: 'audit',
    label: 'Логи действий',
    to: '/audit',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <circle cx="11.5" cy="14.5" r="2.5" />
        <path d="M13.3 16.3L15 18" />
      </svg>
    ),
  },
];

// START_CONTRACT: isMobileShellViewport
//   PURPOSE: Determine whether the viewport width uses the off-canvas mobile/tablet sidebar drawer (<= 1024px).
//   INPUTS: { width: number - Viewport width in CSS pixels }
//   OUTPUTS: { boolean - True when width <= 1024 }
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: isMobileShellViewport
export function isMobileShellViewport(width: number): boolean {
  // START_BLOCK_IS_MOBILE_SHELL_VIEWPORT
  return Number.isFinite(width) && width <= 1024;
  // END_BLOCK_IS_MOBILE_SHELL_VIEWPORT
}

// START_CONTRACT: advanceSecretTapCount
//   PURPOSE: Track rapid taps on the Stealth 404 screen so mobile touch operators can unlock DM login in 3 taps.
//   INPUTS: { prevCount: number, prevTapMs: number, nowMs: number, windowMs?: number }
//   OUTPUTS: { count: number, unlocked: boolean }
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: advanceSecretTapCount
export function advanceSecretTapCount(
  prevCount: number,
  prevTapMs: number,
  nowMs: number,
  windowMs = 900,
): { count: number; unlocked: boolean } {
  // START_BLOCK_ADVANCE_SECRET_TAP_COUNT
  const withinWindow = prevCount > 0 && nowMs - prevTapMs <= windowMs;
  const nextCount = withinWindow ? prevCount + 1 : 1;
  if (nextCount >= 3) {
    return { count: 0, unlocked: true };
  }
  return { count: nextCount, unlocked: false };
  // END_BLOCK_ADVANCE_SECRET_TAP_COUNT
}

// START_CONTRACT: App
//   PURPOSE: Render the 21-screen Admin Workspace shell in MarketPlace dark style or Stealth 404 screen when unauthenticated.
//   INPUTS: none
//   OUTPUTS: React element
//   SIDE_EFFECTS: Exchanges magic-link tokens, loads /admin/me operator identity, and manages responsive sidebar/auth state
//   LINKS: M-ADMIN
// END_CONTRACT: App
export function App() {
  // START_BLOCK_APP
  const [authed, setAuthed] = useState<boolean>(() => !!token.get());
  const [checking, setChecking] = useState<boolean>(() => location.hash.startsWith('#login='));
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileWrapRef = useRef<HTMLDivElement | null>(null);
  const [operatorMe, setOperatorMe] = useState<{
    telegramId: number;
    username: string | null;
    roles: string[];
  } | null>(null);
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const m = location.hash.match(/^#login=(.+)$/);
    if (!m) return;
    const loginToken = decodeURIComponent(m[1]!);
    history.replaceState(null, '', location.pathname);
    void exchangeLogin(loginToken).then((ok) => {
      setAuthed(ok);
      setChecking(false);
    });
  }, []);

  useEffect(() => {
    if (!authed) {
      setOperatorMe(null);
      return;
    }
    let cancelled = false;
    void api
      .get<{ telegramId: number; username: string | null; roles: string[] }>('/admin/me')
      .then((me) => {
        if (!cancelled) setOperatorMe(me);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [authed]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setProfileMenuOpen(false);
  }, [loc.pathname, loc.search]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setProfileMenuOpen(false);
      }
    };
    const onPointerDown = (e: MouseEvent) => {
      if (profileWrapRef.current && !profileWrapRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false);
      }
    };
    const onResize = () => {
      if (!isMobileShellViewport(window.innerWidth)) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, []);

  if (checking || !authed) {
    return <StealthNotFound onAuthenticated={() => setAuthed(true)} />;
  }

  const toggleSidebar = () => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1440;
    if (isMobileShellViewport(vw)) {
      setMobileMenuOpen((v) => !v);
    } else {
      setSidebarHidden((v) => !v);
    }
  };

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const operatorTitle = operatorMe
    ? operatorMe.username
      ? `@${operatorMe.username}`
      : `Оператор #${operatorMe.telegramId}`
    : 'Оператор';

  const operatorRoleLabel = operatorMe?.roles.includes('superadministrator')
    ? 'Суперадмин'
    : operatorMe?.roles.includes('administrator')
      ? 'Администратор'
      : operatorMe?.roles.includes('support')
        ? 'Саппорт'
        : 'Оператор';

  return (
    <ToastProvider>
      <DisplayCurrencyProvider>
        <div
          className={`shell${sidebarHidden ? ' sidebar-collapsed' : ''}${mobileMenuOpen ? ' sidebar-mobile-open' : ''}`}
        >
          {mobileMenuOpen && (
            <div
              className="sidebar-backdrop"
              aria-hidden="true"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          <aside className="sidebar" aria-label="Основная навигация">
            <div className="brand-header">
              <div className="brand-logo">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="brand-title">Stars Fish</div>
                <div className="brand-subtitle">Admin Panel</div>
              </div>
              <button
                type="button"
                className="icon-btn sidebar-close-btn"
                title="Закрыть меню"
                aria-label="Закрыть меню"
                onClick={() => setMobileMenuOpen(false)}
              >
                <AdminIcon name="close" size={17} />
              </button>
            </div>

            <nav className="nav-list">
              {NAV_ENTRIES.map((entry) => {
                if (entry.to) {
                  return (
                    <NavLink
                      key={entry.id}
                      to={entry.to}
                      end={entry.end}
                      className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
                    >
                      {entry.icon}
                      <span>{entry.label}</span>
                    </NavLink>
                  );
                }

                const childActive = Boolean(
                  entry.children?.some((c) => loc.pathname === c.to || loc.pathname.startsWith(c.to + '/')),
                );
                const isOpen = openGroups[entry.id] ?? childActive;

                return (
                  <div className="nav-group" key={entry.id}>
                    <button
                      type="button"
                      className="nav-group-trigger"
                      style={childActive ? { color: '#a78bfa' } : undefined}
                      onClick={() => toggleGroup(entry.id)}
                    >
                      <span className="nav-group-trigger-left">
                        {entry.icon}
                        <span>{entry.label}</span>
                      </span>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{
                          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s ease',
                        }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                    {isOpen && entry.children && (
                      <div className="nav-group-children">
                        {entry.children.map((sub) => (
                          <NavLink
                            key={sub.to}
                            to={sub.to}
                            className={({ isActive }) => 'nav-sublink' + (isActive ? ' active' : '')}
                          >
                            {sub.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            <div className="spacer" />

            <div className="sidebar-copyright">Stars Fish Admin · 2026</div>
          </aside>

          <div className="workspace">
            <header className="topbar">
              <div className="topbar-left">
                <button
                  type="button"
                  className="icon-btn"
                  title="Открыть / свернуть меню"
                  aria-label="Открыть или свернуть меню"
                  onClick={toggleSidebar}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="15" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                </button>
                <GlobalSearch />
              </div>

              <div className="topbar-right">
                <GramUsdRateBadge />
                <DisplayCurrencyToggle />
                <OperatorAlertsMenu />
                <button
                  type="button"
                  className="icon-btn topbar-settings-btn"
                  title="Настройки платформы"
                  aria-label="Настройки платформы"
                  onClick={() => navigate('/settings')}
                >
                  <AdminIcon name="gear" size={17} />
                </button>

                <div ref={profileWrapRef} style={{ position: 'relative' }}>
                  <div
                    className="admin-profile-pill"
                    role="button"
                    tabIndex={0}
                    aria-label="Меню профиля оператора"
                    onClick={() => setProfileMenuOpen((v) => !v)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setProfileMenuOpen((v) => !v);
                      }
                    }}
                  >
                    <div className="admin-avatar">
                      <AdminIcon name="user" size={17} />
                    </div>
                    <div className="admin-profile-text">
                      <div className="admin-profile-name mono">{operatorTitle}</div>
                      <div className="admin-profile-role">{operatorRoleLabel}</div>
                    </div>
                    <svg className="admin-profile-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--ink-faint)' }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>

                  {profileMenuOpen && (
                    <div
                      className="card profile-dropdown"
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 'calc(100% + 8px)',
                        width: 'min(236px, calc(100vw - 24px))',
                        padding: 8,
                        zIndex: 50,
                        boxShadow: '0 18px 40px rgba(0,0,0,0.65)',
                      }}
                    >
                      <div className="profile-dropdown-head">
                        <div className="admin-profile-name mono">{operatorTitle}</div>
                        <div className="admin-profile-role">{operatorRoleLabel}</div>
                      </div>
                      <div
                        className="omnibox-item"
                        onClick={() => {
                          setProfileMenuOpen(false);
                          navigate('/auth-sessions');
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <AdminIcon name="lock" size={14} />
                          <span>Доступы и роли</span>
                        </span>
                      </div>
                      <div
                        className="omnibox-item"
                        onClick={() => {
                          setProfileMenuOpen(false);
                          navigate('/audit');
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <AdminIcon name="shield" size={14} />
                          <span>Журнал действий</span>
                        </span>
                      </div>
                      <div
                        className="omnibox-item"
                        style={{ color: '#f87171' }}
                        onClick={() => {
                          token.clear();
                          location.assign('/');
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <AdminIcon name="logout" size={14} />
                          <span>Выйти из сессии</span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </header>

            <main className="main">
              <PageErrorBoundary key={loc.pathname}>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/payments" element={<Payments />} />
                  <Route path="/users" element={<Users />} />
                  <Route path="/appeals" element={<BanAppeals />} />
                  <Route path="/support" element={<SupportTickets />} />
                  <Route path="/catalog" element={<Catalog />} />
                  <Route path="/games" element={<Games />} />
                  <Route path="/quests" element={<Quests />} />
                  <Route path="/banners" element={<Banners />} />
                  <Route path="/broadcasts" element={<Broadcasts />} />
                  <Route path="/treasury" element={<Treasury />} />
                  <Route path="/transactions" element={<Transactions />} />
                  <Route path="/balance-adjust" element={<BalanceAdjust />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/referrals" element={<Referrals />} />
                  <Route path="/sessions" element={<Sessions />} />
                  <Route path="/fragment" element={<FragmentSession />} />
                  <Route path="/auth-sessions" element={<AuthSessions />} />
                  <Route path="/alerts" element={<Alerts />} />
                  <Route path="/audit" element={<AuditLogs />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </PageErrorBoundary>
            </main>
          </div>
        </div>
      </DisplayCurrencyProvider>
    </ToastProvider>
  );
  // END_BLOCK_APP
}

class PageErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Admin PageErrorBoundary caught:', error, info);
  }

  override render() {
    // START_BLOCK_PAGE_ERROR_BOUNDARY
    if (this.state.error) {
      return (
        <div className="card" style={{ borderColor: 'rgba(239,68,68,0.4)' }}>
          <h2 style={{ marginTop: 0, color: '#f87171' }}>Ошибка отрисовки раздела</h2>
          <p className="muted">{this.state.error.message}</p>
          <button
            type="button"
            className="btn sm"
            onClick={() => this.setState({ error: null })}
          >
            Повторить попытку
          </button>
        </div>
      );
    }
    return this.props.children;
    // END_BLOCK_PAGE_ERROR_BOUNDARY
  }
}

/**
 * Deliberately generic 404 camouflage for unauthenticated visitors.
 * Operators can unlock the hidden 8-digit Telegram DM code login via `#secret-login` or `Ctrl+Shift+L`.
 */
function StealthNotFound({ onAuthenticated }: { onAuthenticated: () => void }) {
  // START_BLOCK_NOT_FOUND
  const [showDmLogin, setShowDmLogin] = useState<boolean>(
    () => location.hash === '#secret-login',
  );
  const [identifier, setIdentifier] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const tapStateRef = useRef<{ count: number; lastMs: number }>({ count: 0, lastMs: 0 });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        setShowDmLogin((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!showDmLogin) {
    return (
      <div
        className="notfound"
        onDoubleClick={() => setShowDmLogin(true)}
        onClick={() => {
          const now = Date.now();
          const next = advanceSecretTapCount(
            tapStateRef.current.count,
            tapStateRef.current.lastMs,
            now,
          );
          tapStateRef.current = { count: next.count, lastMs: now };
          if (next.unlocked) setShowDmLogin(true);
        }}
      >
        <h1>404 Not Found</h1>
        <p>The requested URL was not found on this server.</p>
      </div>
    );
  }

  const handleRequestCode = async () => {
    if (!identifier.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await startDmLogin(identifier.trim());
      setChallengeId(res.challengeId);
    } catch (e) {
      setErr((e as Error).message || 'Не удалось запросить код входа');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!challengeId || !code.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const ok = await verifyDmLogin(challengeId, code.trim());
      if (ok) {
        onAuthenticated();
      } else {
        setErr('Неверный или истёкший код');
      }
    } catch (e) {
      setErr((e as Error).message || 'Ошибка проверки кода');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card" style={{ maxWidth: 420 }}>
        <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AdminIcon name="lock" size={17} />
          <span>Вход оператора по коду из Telegram</span>
        </h2>
        <p className="muted" style={{ fontSize: 13 }}>
          Введите ваш Telegram ID (из списка операторов), чтобы получить одноразовый 8-значный код в личные сообщения от бота.
        </p>

        {!challengeId ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              placeholder="Ваш Telegram ID (например: 123456789)"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
            <button
              type="button"
              className="btn primary"
              disabled={!identifier.trim() || busy}
              onClick={() => void handleRequestCode()}
            >
              {busy ? 'Отправка…' : 'Получить 8-значный код в боте'}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              placeholder="8-значный код из Telegram DM"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <button
              type="button"
              className="btn primary"
              disabled={!code.trim() || busy}
              onClick={() => void handleVerifyCode()}
            >
              {busy ? 'Проверка…' : 'Войти в панель'}
            </button>
          </div>
        )}

        {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
      </div>
    </div>
  );
  // END_BLOCK_NOT_FOUND
}
